import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Server, 
  Globe,
  ChevronDown, 
  ChevronUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit,
  Save,
  X,
  Smartphone,
  MonitorSmartphone,
  Tablet
} from 'lucide-react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const SettingsPage = ({ user, setCurrentPage }) => {
  // States for boards
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [expandedSection, setExpandedSection] = useState('boards'); // 'boards', 'servers', or 'general'

  // States for server status
  const [paymentServerStatus, setPaymentServerStatus] = useState({
    status: 'unknown', // 'running', 'down', 'unknown'
    lastChecked: null
  });
  const [layoutServerStatus, setLayoutServerStatus] = useState({
    status: 'unknown', // 'running', 'down', 'unknown'
    lastChecked: null
  });
  const [checkingServer, setCheckingServer] = useState(false);
  
  // States for maintenance mode
  const [maintenanceSettings, setMaintenanceSettings] = useState({
    web: false,
    ios: false,
    android: false,
    message: "We're currently performing scheduled maintenance. Please check back later."
  });
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  // Server URLs (using your actual layout server URL)
  const serverUrls = {
    payment: 'https://examcraft-server.onrender.com/health', // Replace with actual payment server URL when available
    layout: 'https://examcraft-layout-server.online/health'
  };

  // Check server status on component mount
  useEffect(() => {
    // Check both server statuses when component mounts
    checkServerStatus('layout');
    checkServerStatus('payment');
    
    // Set up interval to periodically check layout server status (every 60 seconds)
    const layoutServerInterval = setInterval(() => {
      checkServerStatus('layout');
    }, 60000);
    
    // Clean up interval on component unmount
    return () => clearInterval(layoutServerInterval);
  }, []);

  // Fetch boards and maintenance settings on component mount
  useEffect(() => {
    fetchBoards();
    fetchMaintenanceSettings();
  }, []);

  // Fetch boards from Firestore
  const fetchBoards = async () => {
    try {
      setLoading(true);
      const boardsRef = collection(db, "boards");
      const snapshot = await getDocs(boardsRef);
      const boardsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        isPublished: doc.data().isPublished !== false // Default to true if not set
      }));
      setBoards(boardsList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching boards:", error);
      setLoading(false);
    }
  };
  
  // Fetch maintenance settings from Firestore
  const fetchMaintenanceSettings = async () => {
    try {
      const maintenanceRef = doc(db, "settings", "maintenance");
      const maintenanceDoc = await getDoc(maintenanceRef);
      
      if (maintenanceDoc.exists()) {
        const data = maintenanceDoc.data();
        setMaintenanceSettings({
          web: data.web || false,
          ios: data.ios || false,
          android: data.android || false,
          message: data.message || "We're currently performing scheduled maintenance. Please check back later."
        });
      }
    } catch (error) {
      console.error("Error fetching maintenance settings:", error);
    }
  };

  // Toggle board publication status
  const toggleBoardPublishStatus = async (boardId, currentStatus) => {
    try {
      const boardRef = doc(db, "boards", boardId);
      await updateDoc(boardRef, {
        isPublished: !currentStatus,
        lastUpdated: new Date()
      });
      
      // Update local state
      setBoards(boards.map(board => 
        board.id === boardId 
          ? { ...board, isPublished: !currentStatus } 
          : board
      ));
    } catch (error) {
      console.error("Error updating board status:", error);
      alert("Failed to update board status. Please try again.");
    }
  };

  // Start editing board name
  const startEditingBoard = (boardId) => {
    setEditingBoardId(boardId);
  };

  // Save board name changes
  const saveBoardName = async (boardId, newName) => {
    try {
      const boardRef = doc(db, "boards", boardId);
      await updateDoc(boardRef, {
        name: newName,
        lastUpdated: new Date()
      });
      
      // Update local state
      setBoards(boards.map(board => 
        board.id === boardId 
          ? { ...board, name: newName } 
          : board
      ));
      
      setEditingBoardId(null);
    } catch (error) {
      console.error("Error updating board name:", error);
      alert("Failed to update board name. Please try again.");
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingBoardId(null);
  };

  // Update board name in state (without saving to db yet)
  const updateBoardName = (boardId, newName) => {
    setBoards(boards.map(board => 
      board.id === boardId 
        ? { ...board, editName: newName } 
        : board
    ));
  };

  // Check server status
  const checkServerStatus = async (serverType) => {
    const serverUrl = serverUrls[serverType];
    
    if (!serverUrl) {
      console.error(`No URL defined for ${serverType} server`);
      return;
    }
    
    if (serverType === 'layout') {
      setCheckingServer(true);
    }
    
    try {
      // Make an actual API call to check server health
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(serverUrl, {
        method: 'GET',
        signal: controller.signal,
        mode: 'cors', // May need to adjust based on your server CORS settings
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      const isRunning = response.ok;
      
      if (serverType === 'payment') {
        setPaymentServerStatus({
          status: isRunning ? 'running' : 'down',
          lastChecked: new Date()
        });
      } else if (serverType === 'layout') {
        setLayoutServerStatus({
          status: isRunning ? 'running' : 'down',
          lastChecked: new Date()
        });
        setCheckingServer(false);
      }
      
    } catch (error) {
      console.error(`Error checking ${serverType} server status:`, error);
      
      if (serverType === 'payment') {
        setPaymentServerStatus({
          status: 'down',
          lastChecked: new Date(),
          error: error.message
        });
      } else if (serverType === 'layout') {
        setLayoutServerStatus({
          status: 'down',
          lastChecked: new Date(),
          error: error.message
        });
        setCheckingServer(false);
      }
    }
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  // Handle maintenance mode toggle for a specific platform
  const toggleMaintenanceMode = (platform) => {
    setMaintenanceSettings(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };
  
  // Handle maintenance message change
  const handleMaintenanceMessageChange = (e) => {
    setMaintenanceSettings(prev => ({
      ...prev,
      message: e.target.value
    }));
  };
  
  // Save maintenance settings to Firestore
  const saveMaintenanceSettings = async () => {
    try {
      setSavingMaintenance(true);
      
      const maintenanceRef = doc(db, "settings", "maintenance");
      await setDoc(maintenanceRef, {
        ...maintenanceSettings,
        lastUpdated: new Date()
      });
      
      alert("Maintenance settings saved successfully!");
      setSavingMaintenance(false);
    } catch (error) {
      console.error("Error saving maintenance settings:", error);
      alert("Failed to save maintenance settings. Please try again.");
      setSavingMaintenance(false);
    }
  };

  // Get status indicator component based on status
  const getStatusIndicator = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'down':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="w-7 h-7 mr-2 text-gray-700" />
            App Settings
          </h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Boards Management Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div 
            className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection('boards')}
          >
            <div className="flex items-center">
              <Globe className="w-5 h-5 mr-2 text-gray-700" />
              <h2 className="text-lg font-medium text-gray-900">Boards Management</h2>
            </div>
            {expandedSection === 'boards' ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </div>
          
          {expandedSection === 'boards' && (
            <div className="p-6">
              <p className="text-gray-500 mb-4">Control which boards are published and available to users in the application.</p>
              
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Board Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Updated
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {boards.map((board) => (
                          <tr key={board.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingBoardId === board.id ? (
                                <input 
                                  type="text" 
                                  className="w-full p-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={board.editName !== undefined ? board.editName : board.name}
                                  onChange={(e) => updateBoardName(board.id, e.target.value)}
                                  autoFocus
                                />
                              ) : (
                                <div className="text-sm font-medium text-gray-900">{board.name}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                board.isPublished 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {board.isPublished ? 'Published' : 'Not Published'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {board.lastUpdated 
                                ? new Date(board.lastUpdated.seconds * 1000).toLocaleString() 
                                : 'Never'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {editingBoardId === board.id ? (
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => saveBoardName(board.id, board.editName !== undefined ? board.editName : board.name)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    <Save className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <X className="h-5 w-5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex justify-end gap-3">
                                  <button
                                    onClick={() => startEditingBoard(board.id)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    <Edit className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => toggleBoardPublishStatus(board.id, board.isPublished)}
                                    className={`${
                                      board.isPublished 
                                        ? 'text-yellow-600 hover:text-yellow-800' 
                                        : 'text-green-600 hover:text-green-800'
                                    }`}
                                  >
                                    {board.isPublished ? 'Unpublish' : 'Publish'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {boards.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No boards found. Add boards from the Boards Management section.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Server Status Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div 
            className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection('servers')}
          >
            <div className="flex items-center">
              <Server className="w-5 h-5 mr-2 text-gray-700" />
              <h2 className="text-lg font-medium text-gray-900">Server Status</h2>
            </div>
            {expandedSection === 'servers' ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </div>
          
          {expandedSection === 'servers' && (
            <div className="p-6">
              <p className="text-gray-500 mb-4">Monitor the status of application servers and services.</p>
              
              <div className="space-y-6">
                {/* Payment Server */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                      <div className="mr-3">
                        {getStatusIndicator(paymentServerStatus.status)}
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Payment Server</h3>
                        <p className="text-sm text-gray-500">Handles payment processing and subscription management</p>
                      </div>
                    </div>
                    <button
                      onClick={() => checkServerStatus('payment')}
                      disabled={checkingServer}
                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium ${
                        checkingServer 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                      }`}
                    >
                      <RefreshCw className={`h-4 w-4 ${checkingServer ? 'animate-spin' : ''}`} />
                      Check Status
                    </button>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span className={`font-medium ${
                        paymentServerStatus.status === 'running' 
                          ? 'text-green-600' 
                          : paymentServerStatus.status === 'down' 
                            ? 'text-red-600' 
                            : 'text-gray-500'
                      }`}>
                        {paymentServerStatus.status === 'running' 
                          ? 'Running' 
                          : paymentServerStatus.status === 'down' 
                            ? 'Not Responding' 
                            : 'Unknown'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Last Checked:</span>
                      <span className="text-gray-700">
                        {paymentServerStatus.lastChecked 
                          ? paymentServerStatus.lastChecked.toLocaleString() 
                          : 'Never'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Server URL:</span>
                      <span className="text-gray-700 font-mono text-xs">
                        {serverUrls.payment}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Layout Server */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                      <div className="mr-3">
                        {layoutServerStatus.status === 'running' ? (
                          <div className="relative">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full"></span>
                          </div>
                        ) : (
                          <div className="relative">
                            <XCircle className="h-5 w-5 text-red-500" />
                            <span className="absolute bottom-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Layout Server</h3>
                        <p className="text-sm text-gray-500">Manages content layout and rendering</p>
                      </div>
                    </div>
                    {/* No manual refresh button - status updates automatically */}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span className={`font-medium ${
                        layoutServerStatus.status === 'running' 
                          ? 'text-green-600' 
                          : layoutServerStatus.status === 'down' 
                            ? 'text-red-600' 
                            : 'text-gray-500'
                      }`}>
                        {layoutServerStatus.status === 'running' 
                          ? 'Running' 
                          : layoutServerStatus.status === 'down' 
                            ? 'Not Responding' 
                            : 'Unknown'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Last Checked:</span>
                      <span className="text-gray-700">
                        {layoutServerStatus.lastChecked 
                          ? layoutServerStatus.lastChecked.toLocaleString() 
                          : 'Never'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Server URL:</span>
                      <span className="text-gray-700 font-mono text-xs">
                        {serverUrls.layout}
                      </span>
                    </div>
                    
                    {layoutServerStatus.error && (
                      <div className="mt-2 text-xs text-red-600">
                        Error: {layoutServerStatus.error}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  <p>
                    The layout server status is checked automatically every minute. The payment server status will be implemented when the endpoint is available.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* General Settings Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div 
            className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection('general')}
          >
            <div className="flex items-center">
              <Settings className="w-5 h-5 mr-2 text-gray-700" />
              <h2 className="text-lg font-medium text-gray-900">General Settings</h2>
            </div>
            {expandedSection === 'general' ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </div>
          
          {expandedSection === 'general' && (
            <div className="p-6">
              <p className="text-gray-500 mb-4">Configure general application settings.</p>
              
              {/* This is a placeholder for additional settings you might want to add */}
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-3">Server Configuration</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Server URL
                      </label>
                      <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                        placeholder="https://payment-server.example.com"
                        value={serverUrls.payment}
                        readOnly
                      />
                      <p className="mt-1 text-xs text-gray-500">You will be able to edit this field when real implementation is in place.</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Layout Server URL
                      </label>
                      <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                        placeholder="https://layout-server.example.com"
                        value={serverUrls.layout}
                        readOnly
                      />
                      <p className="mt-1 text-xs text-gray-500">You will be able to edit this field when real implementation is in place.</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 pb-6 border-b border-gray-200">
                  <h3 className="text-md font-medium text-gray-900 mb-3">Maintenance Mode</h3>
                  <p className="mb-4 text-sm text-gray-500">
                    Enable maintenance mode to temporarily disable the application on specific platforms while performing updates.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Web Platform */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <MonitorSmartphone className="h-5 w-5 text-gray-600 mr-2" />
                        <div>
                          <h4 className="font-medium text-gray-800">Web Application</h4>
                          <p className="text-xs text-gray-500">
                            Put the web application into maintenance mode
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={maintenanceSettings.web}
                          onChange={() => toggleMaintenanceMode('web')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    {/* iOS Platform */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <Smartphone className="h-5 w-5 text-gray-600 mr-2" />
                        <div>
                          <h4 className="font-medium text-gray-800">iOS Application</h4>
                          <p className="text-xs text-gray-500">
                            Put the iOS application into maintenance mode
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={maintenanceSettings.ios}
                          onChange={() => toggleMaintenanceMode('ios')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    {/* Android Platform */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <Tablet className="h-5 w-5 text-gray-600 mr-2" />
                        <div>
                          <h4 className="font-medium text-gray-800">Android Application</h4>
                          <p className="text-xs text-gray-500">
                            Put the Android application into maintenance mode
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={maintenanceSettings.android}
                          onChange={() => toggleMaintenanceMode('android')}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Maintenance Message</h4>
                    <textarea 
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      rows={3}
                      placeholder="Enter a maintenance message to display to users..."
                      value={maintenanceSettings.message}
                      onChange={handleMaintenanceMessageChange}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This message will be displayed to users on platforms that are in maintenance mode.
                    </p>
                  </div>
                  
                  <div className="mt-6">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-amber-800">Important Information</h4>
                          <p className="text-xs text-amber-700 mt-1">
                            When maintenance mode is enabled for a platform, users on that platform will see the maintenance message and will not be able to access the application until maintenance mode is disabled.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-3">
                      <div className="text-sm text-gray-500">
                        {(maintenanceSettings.web || maintenanceSettings.ios || maintenanceSettings.android) ? (
                          <span className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-amber-500 mr-1" />
                            {[
                              maintenanceSettings.web ? 'Web' : '',
                              maintenanceSettings.ios ? 'iOS' : '',
                              maintenanceSettings.android ? 'Android' : ''
                            ].filter(Boolean).join(', ')} currently in maintenance mode
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                            All platforms are active
                          </span>
                        )}
                      </div>
                      
                      <button 
                        className={`px-4 py-2 rounded-lg flex items-center ${
                          savingMaintenance 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        onClick={saveMaintenanceSettings}
                        disabled={savingMaintenance}
                      >
                        {savingMaintenance && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                        Save Maintenance Settings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;